import React from "react";
import { classNames } from "@emisgroup/ui-core";
import { Table } from "@emisgroup/ui-table";
import {
  ColumnDef,
  ColumnMeta,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Header,
  RowData,
  SortDirection,
  useReactTable,
} from "@tanstack/react-table";
import { SortableTableProps } from "./Types";
import TriangleDown from "~icons/ic/outline-arrow-drop-down";
import TriangleUp from "~icons/ic/outline-arrow-drop-up";
import "./Style.scss";

/**
 * A table that can be sorted by pre-configured methods
 * @param SortableTableProps The configuration for the sortable table
 * @returns A sortable table component
 */
export const SortableTable = ({
  columnData,
  columnConfig,
  onSortChange,
  rowData,
  sortState,
  stickyHeader = false,
  tableConfig,
  onRowSelected,
  ...rest
}: SortableTableProps): JSX.Element => {
  const columnsDef = React.useMemo<ColumnDef<any>[]>(
    () => {
      // If we have a custom configuration, use that
      if (columnConfig) {
        return columnConfig;
      }

      // Otherwise, generate a default configuration
      return columnData.map((column) => ({
        accessorKey: column.key,
        cell: ({ row }) => column.cellFunction?.(row),
        enableSorting: column.sortable ?? true,
        header: column.label,
        invertSorting: column.invertSort ?? false,
        meta: column.meta,
        sortingFn: column.customSort ?? "alphanumeric",
        sortUndefined: column.undefinedToBottom ? "last" : "first",
      }));
    },
    []
  );

  const tableDef = useReactTable({
    columns: columnsDef,
    data: rowData,
    state: {
      sorting: sortState,
    },
    onSortingChange: onSortChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...tableConfig,
  });

  const getHeaderCell = (header: Header<any, unknown>): JSX.Element => {
    const headerCellClasses = classNames(
      "sortable-table-header",
      header.column.getCanSort() && "sortable-table-header-sortable",
    );

    const headerTitle = header.column.columnDef.header?.toString();

    const getSortingTitle = (sortingOrder: false | SortDirection): string => {
      if (!sortingOrder) {
        return " (Unsorted)";
      }

      if (sortingOrder === "asc") {
        return " (Ascending)";
      }

      return " (Descending)";
    };

    const getSortingIcon = (sortingOrder: false | SortDirection): JSX.Element | null => {
      if (!sortingOrder) {
        return null;
      }

      if (sortingOrder === "asc") {
        return <TriangleUp size="small" title="" />;
      }

      return <TriangleDown size="small" title="" />;
    };

    const columnMeta: ColumnMeta<RowData, any> | undefined = header.column.columnDef.meta;

    return <th
      key={header.id}
      aria-label={columnMeta?.ariaLabel}
      className={columnMeta?.className}
      colSpan={header.colSpan}
      style={columnMeta?.style}>
      {header.column.getCanSort() ? (
        <button
          className={headerCellClasses}
          onClick={header.column.getToggleSortingHandler()}
          title={`${headerTitle}${getSortingTitle(header.column.getIsSorted())}`}>
          {flexRender(header.column.columnDef.header, header.getContext())}
          {getSortingIcon(header.column.getIsSorted())}
        </button>
      ) : (
        <div title={headerTitle}>
          {flexRender(header.column.columnDef.header, header.getContext())}
        </div>
      )}
    </th>;
  };

  return (
    <Table {...rest}>
      <thead>
        {tableDef.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} data-sticky-row={stickyHeader}>
            {headerGroup.headers.map(header => getHeaderCell(header))}
          </tr>
        ))}
      </thead>
      <tbody>
        {tableDef.getRowModel().rows.map((row) => (
          <tr key={row.id} onClick={onRowSelected} style={{ cursor: onRowSelected ? "pointer" : "" }}>
            {row.getAllCells().map((cell) => {
              // Render datetime values as a string
              if (cell.column.columnDef.sortingFn === "datetime") {
                const dateRenderFunc = cell.column.columnDef.meta?.dateRender;

                if (!dateRenderFunc) {
                  throw new Error("A column with 'datetime' sorting requires a " +
                      "dateRender function inside the meta object");
                }

                return (
                  <td key={cell.id}>
                    {dateRenderFunc(cell.renderValue<Date>())}
                  </td>
                );
              }

              // Render string values as a title
              const cellTitle = typeof cell.getValue() === "string" ? cell.renderValue<string>() : "";

              return (
                <td key={cell.id} title={cellTitle}>
                  {cell.renderValue<JSX.Element>()}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};
